/**
 * `.nozomi` decoder / fetcher.
 *
 * Hitomi.la publishes precomputed lists of gallery IDs as binary files under
 * `https://ltn.hitomi.la/n/...`. The files are packed `int32` big-endian
 * arrays; a 100-entry file is exactly 400 bytes, a 25-entry slice is 100
 * bytes, and so on.
 *
 * URL conventions:
 *
 *   `/n/<area>/[orderBy/][type:]<name>-<lang>.nozomi`  (tagged)
 *   `/n/<orderBy-or-'index'>-<lang>.nozomi`            (untagged)
 *
 * For pagination we issue HTTP Range requests; the file is essentially a
 * fixed-stride record so byte ranges map cleanly to record ranges.
 */

import { NetworkError, ParseError } from '../../lib/errors.ts';
import { RESOURCE_DOMAIN } from './gg.ts';

/** Bytes per ID record in a `.nozomi` file. */
export const NOZOMI_RECORD_SIZE = 4;

/**
 * Parse a `.nozomi` body into the list of gallery IDs it encodes.
 *
 * Pure: takes any `ArrayBufferLike` (e.g. from `Response.arrayBuffer()`,
 * `Buffer.buffer`, or hand-rolled in tests) and reads big-endian int32s.
 *
 * Trailing bytes that don't form a complete record are silently ignored —
 * Hitomi has historically not padded, but defensive decoding keeps callers
 * from blowing up on a partial Range response.
 */
export function parseNozomi(buffer: ArrayBuffer | ArrayBufferView): number[] {
  const view =
    ArrayBuffer.isView(buffer)
      ? new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength)
      : new DataView(buffer);
  const count = Math.floor(view.byteLength / NOZOMI_RECORD_SIZE);
  const out: number[] = new Array(count);
  for (let i = 0; i < count; i++) {
    out[i] = view.getInt32(i * NOZOMI_RECORD_SIZE, false /* big-endian */);
  }
  return out;
}

/**
 * Build the `.nozomi` URL path for a single query. Mirrors the upstream
 * `createNozomiUrl` logic but returns a full URL string (the proxy doesn't
 * care about `[hostname, path]` tuples).
 */
export function nozomiUrl(opts: NozomiUrlOpts): string {
  const language = opts.language ?? 'all';
  const orderBy = opts.orderBy ?? '';

  if (!opts.tag) {
    const head = orderBy || 'index';
    return `https://${RESOURCE_DOMAIN}/n/${head}-${language}.nozomi`;
  }

  let area: string;
  let prefix = orderBy ? `${orderBy}/` : '';

  if (opts.tag.namespace === 'male' || opts.tag.namespace === 'female') {
    area = 'tag/';
    prefix += `${opts.tag.namespace}:`;
  } else {
    area = `${opts.tag.namespace}/`;
  }

  const name = encodeURIComponent(opts.tag.name);
  return `https://${RESOURCE_DOMAIN}/n/${area}${prefix}${name}-${language}.nozomi`;
}

export interface NozomiUrlOpts {
  /**
   * Optional tag filter. When omitted, the URL resolves to the "global"
   * index for the language (popular or all-gallery).
   */
  tag?: { namespace: string; name: string };
  /**
   * `'popular/today'` | `'popular/week'` | ... | `'date/published'` | `''`.
   * Empty string means "default order" (date added, descending).
   */
  orderBy?: string;
  /**
   * `'all'` for cross-language, otherwise the lowercase hitomi language slug
   * (e.g. `'english'`, `'japanese'`).
   */
  language?: string;
}

export interface FetchNozomiOpts extends NozomiUrlOpts {
  /** Zero-based page index. */
  page?: number;
  /** Items per page. */
  pageSize?: number;
  /** Optional override for the page-size multiplier used by upstream. */
  oversample?: number;
  signal?: AbortSignal;
}

/**
 * One page of nozomi IDs. `hasMore` is best-effort: hitomi doesn't tell us
 * the total in the response body, so we set `hasMore = true` whenever the
 * page is fully populated.
 */
export interface NozomiPage {
  ids: number[];
  page: number;
  hasMore: boolean;
  /** Total entries inferred from `Content-Range`, if present. */
  totalItems?: number;
}

/**
 * Fetch a single page of gallery IDs from a `.nozomi` file via the proxy.
 *
 * Hitomi's nozomi pages are not the same as our UI pages — the upstream takes
 * `pageSize * 4` extra records and post-filters. We follow that convention so
 * tag-intersection callers have headroom; the public-facing index pages
 * (`searchByTag` / `listPopular`) request just one page worth.
 */
export async function fetchNozomi(opts: FetchNozomiOpts): Promise<NozomiPage> {
  const page = opts.page ?? 0;
  const pageSize = opts.pageSize ?? 25;
  const oversample = opts.oversample ?? 1;
  const recordCount = pageSize * oversample;
  const start = page * recordCount * NOZOMI_RECORD_SIZE;
  const end = start + recordCount * NOZOMI_RECORD_SIZE - 1;

  const url = nozomiUrl(opts);
  const headers: Record<string, string> = {
    Range: `bytes=${start}-${end}`,
  };

  const { proxyFetch } = await import('@/lib/proxy');
  const init: { headers: Record<string, string>; signal?: AbortSignal } = { headers };
  if (opts.signal) init.signal = opts.signal;
  const res = await proxyFetch(url, init);

  if (res.status !== 200 && res.status !== 206) {
    if (res.status === 404) {
      // Empty / unknown filter — return an empty page.
      return { ids: [], page, hasMore: false };
    }
    throw new NetworkError(`hitomi: nozomi ${url} returned ${res.status}`, {
      source: 'hitomi',
      status: res.status,
    });
  }

  const buf = await res.arrayBuffer();
  let ids: number[];
  try {
    ids = parseNozomi(buf);
  } catch (err) {
    throw new ParseError('hitomi: malformed nozomi body', {
      source: 'hitomi',
      cause: err,
    });
  }

  const result: NozomiPage = {
    ids,
    page,
    hasMore: ids.length >= recordCount,
  };
  const total = extractTotalFromContentRange(res.headers.get('content-range'));
  if (total !== undefined) {
    result.totalItems = Math.floor(total / NOZOMI_RECORD_SIZE);
  }
  return result;
}

/**
 * `Content-Range: bytes start-end/total` — extract the `total`. Returns
 * undefined for `*` or missing headers.
 */
function extractTotalFromContentRange(header: string | null): number | undefined {
  if (!header) return undefined;
  const m = /\/(\d+)\s*$/.exec(header);
  if (!m || !m[1]) return undefined;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Translate our `PopularOpts.range` into the `orderBy` slug used in the
 * `/n/popular/<range>-<lang>.nozomi` URL.
 */
export function popularRangeOrderBy(range: 'today' | 'week' | 'month' | 'year'): string {
  return `popular/${range}`;
}
